import { useEffect } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { PaginatedVenuesResponse } from '@lov/shared';
import { validatedApi } from '../lib/api/validated-client';
import type { VenueResponse } from '../features/venues/hooks/useVenues';
import { VenueContext, VENUE_TYPE_HINT_KEY } from '../contexts/VenueContext';
import { VendorLoadingFallback } from './loading/VendorLoadingFallback';
import { VenueLoadError } from './loading/VenueLoadError';

export function VenueLayout() {
  const { venueId } = useParams<{ venueId: string }>();

  const {
    data: venues,
    isLoading,
    isError,
    refetch,
  } = useQuery<PaginatedVenuesResponse, Error, VenueResponse[]>({
    queryKey: ['venues', 'my'],
    queryFn: () => validatedApi.get('/venues/my'),
    select: (r) => r.data,
    staleTime: 5 * 60 * 1000,
  });

  const venue = venues?.find((v) => v.id === venueId);

  // Persist venue type hint for Suspense fallbacks — must be called unconditionally
  // before any conditional returns to satisfy rules of hooks.
  useEffect(() => {
    if (!venue) return;
    try {
      localStorage.setItem(VENUE_TYPE_HINT_KEY, venue.venueType);
    } catch {
      // Ignore storage errors
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venue?.venueType]);

  if (isLoading) return <VendorLoadingFallback />;
  if (isError) return <VenueLoadError onRetry={() => refetch()} />;
  if (!venues?.length) return <Navigate to="/vendor/venues" replace />;

  // Guard: if venueId is not a UUID (e.g., React Router matched a static path segment
  // like "settings" as the :venueId param), redirect to /vendor/<segment> so the literal
  // route takes over — e.g. /vendor/settings correctly renders VendorSettingsPage.
  const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (venueId && !UUID_REGEX.test(venueId)) {
    return <Navigate to={`/vendor/${venueId}`} replace />;
  }

  if (!venue) {
    return <Navigate to={`/vendor/${venues[0].id}/dashboard`} replace />;
  }

  return (
    <VenueContext.Provider value={{ venueId: venue.id, venue, venues }}>
      <Outlet />
    </VenueContext.Provider>
  );
}
